import { useEffect, useMemo, useState } from "react";
import { SharedContentType } from "../../../api/auth";
import { HStack } from "../../../shared/utils";
import { useSharedContentToken } from "../../../queries/auth";
import { getSharedURL } from "./SharedTableAgComponents";
import { useAuthStore } from "../../../store/useAuthStore";
import IframePopupDialog from "./IFramePopup";
import { formatDate } from "../../../utils/date";
import { indexOf } from "lodash";
import { handleClickForTracking } from "../../../analytics";
import { AnalyticsConstant } from "../../../AnalyticsContant";
import { trackEvent } from "../../../utils/AmpAnalytics";

export enum CaptableSharedResource {
  ROUND_TRANSACTION = "ROUND TRANSACTION",
  ROUND_CREATION_SUMMARY = "ROUND CREATION SUMMARY",
  DASHBOARD = "DASHBOARD",
  NON_PRICED_ROUND_MODEL = "NON PRICED ROUND MODEL",
  PRICED_ROUND_MODEL = "PRICED ROUND MODEL",
}

type PlatformType = "CAPTABLE" | "ESOP" | "PORTFOLIO" | "TASK_MANAGER";

export const bgColor: Record<PlatformType, { bg: string; img: string }> = {
  CAPTABLE: { bg: "#E6EFFF", img: "/media/switchProductIcons/captable.svg" },
  ESOP: { bg: "#FAF9F3", img: "/media/switchProductIcons/esop.svg" },
  PORTFOLIO: { bg: "#E9FBF3", img: "/media/switchProductIcons/portfolio.svg" },
  TASK_MANAGER: {
    bg: "#FEF2E7",
    img: "/media/switchProductIcons/task_manager.svg",
  },
};

export function SharedPreviewCards({ data }: { data: any[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogKey, setDialogKey] = useState(0); // State to track dialog key
  const { mutate: getSharedContentToken } = useSharedContentToken();
  const { accessToken } = useAuthStore();
  const [currentPage, setCurrentPage] = useState<SharedContentType>();
  const [imageErrorIndex, setImageErrorIndex] = useState<any>();
  const [resourceId, setResourceId] = useState<any>();
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (currentPage && resourceId)
      getSharedContentToken(
        {
          data: currentPage,
          token: `${accessToken}`,
        },
        {
          onSuccess: (tokenData) => {
            const currentUrl = getSharedURL(
              tokenData.data,
              currentPage.platform,
              currentPage.resourceId
            );
            setUrl(currentUrl);
          },
          onError: () => {},
        }
      );
  }, [resourceId]);

  const handleCardClick = (page: SharedContentType) => {
    // handleClickForTracking(AnalyticsConstant.shared_content_view_button);
    trackEvent(
      `Viewing Shared Page${page.companyName ? ` - ${page.companyName}` : ""}`
    );
    setCurrentPage(page);
    setResourceId(page.resourceId);
    setIsOpen(true);
    setDialogKey((prevKey) => prevKey + 1); // Increment dialog key to force re-render
  };

  return (
    <>
      <div
        className="flex gap-6 overflow-x-auto whitespace-nowrap scrollbar-hide mb-4 py-2"
        style={{
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {data?.map((page: SharedContentType, index: number) => {
          const imageUrl = `/media/sharedPageImages/${page.platform}_${
            page.resourceType as CaptableSharedResource
          }.png`;

          return (
            <div
              key={index}
              className="bg-gray-100 rounded-lg p-4 h-[250px] w-[320px] flex-shrink-0 cursor-pointer hover-effect hover:border"
              onClick={() => handleCardClick(page)}
            >
              <p className="mb-2 text-md font-semi_medium text-gray-500 truncate">{`${
                page.companyName
                  ? `${page.resourceType} - ${page.companyName}`
                  : page.resourceType || "Shared Page"
              }`}</p>
              <div className="min-h-[160px] border rounded-lg">
                {imageErrorIndex !== index ? (
                  <img
                    src={imageUrl}
                    alt="Shared Page Preview"
                    className="w-full h-[160px] rounded-lg brightness-130"
                    onError={() => {
                      setImageErrorIndex(index);
                    }}
                  />
                ) : (
                  <div
                    className="w-full h-[160px] flex items-center justify-center rounded-lg"
                    style={{
                      backgroundColor:
                        bgColor[page.platform as PlatformType]?.bg,
                    }}
                  >
                    <img
                      src={bgColor[page.platform as PlatformType]?.img}
                      alt="Shared Page Preview"
                      className="w-[50px] h-[50px] rounded-lg brightness-130"
                      onError={() => {
                        setImageErrorIndex(index);
                      }}
                    />
                  </div>
                )}
              </div>
              <HStack className="mt-2 text-gray-400 gap-2 text-sm">
                <p className="">{formatDate(page.createdAt)}</p>
              </HStack>
            </div>
          );
        })}
      </div>
      {url && url !== "" && isOpen && (
        <IframePopupDialog
          name={currentPage ? currentPage?.resourceType : ""}
          link={url}
          key={dialogKey} // Use dialogKey to force re-render
          companyName={currentPage?.companyName}
          open={isOpen}
          label={false}
          close={() => setIsOpen(false)}
          onClick={() => {}}
        />
      )}
    </>
  );
}
